import { useState, useRef, useCallback, type KeyboardEvent, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface ChatInputProps {
  /** 送信時のコールバック */
  onSend: (message: string) => void;
  /** 送信中かどうか */
  disabled?: boolean;
  /** プレースホルダー */
  placeholder?: string;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チャット入力フォーム。自動リサイズテキストエリアとCmd/Ctrl+Enter送信をサポートし、多言語プレースホルダー対応
 * @graph-connects none
 */
export function ChatInput({ onSend, disabled = false, placeholder }: ChatInputProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      if (input.trim() && !disabled) {
        onSend(input.trim());
        setInput('');
        // テキストエリアの高さをリセット
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      }
    },
    [input, disabled, onSend],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Cmd/Ctrl+Enter で送信、それ以外のEnterは改行
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit],
  );

  const handleInput = useCallback(() => {
    // テキストエリアの高さを自動調整
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${String(Math.min(textareaRef.current.scrollHeight, 120))}px`;
    }
  }, []);

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-2">
      <div className="flex-1 relative">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder={placeholder ?? t('chat.inputPlaceholder', '質問を入力...')}
          disabled={disabled}
          rows={1}
          className="
            w-full
            bg-[var(--color-bg-surface)]
            border border-[var(--color-border)]
            rounded-lg
            px-4 py-2.5
            text-sm
            text-[var(--color-text-primary)]
            placeholder:text-[var(--color-text-muted)]
            resize-none
            transition-all duration-[var(--transition-fast)]
            focus:outline-none focus:ring-2 focus:ring-[var(--color-border-focus)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-base)]
            hover:border-[var(--color-border-hover)]
            disabled:opacity-50 disabled:cursor-not-allowed
          "
          aria-label={t('chat.inputLabel', 'チャット入力')}
        />
      </div>
      <button
        type="submit"
        disabled={disabled || !input.trim()}
        className="
          flex-shrink-0
          px-4 py-2.5
          bg-[var(--color-accent-primary)]
          hover:bg-[var(--color-accent-hover)]
          active:bg-[var(--color-accent-active)]
          text-white text-sm font-medium
          rounded-lg
          transition-all duration-[var(--transition-fast)]
          disabled:opacity-50 disabled:cursor-not-allowed
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-base)]
        "
        aria-label={t('chat.send', '送信')}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-5 h-5"
          aria-hidden="true"
        >
          <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
        </svg>
      </button>
    </form>
  );
}
